import { tl } from 'common/services/i18nService';
import { UserPrivileges } from 'common/store/session/constants';
import { HitData, SearchColumn } from 'common/store/common/search/types';
import { getWorkspaceType, isWorkflowOverlayOpen } from 'common/services/permissions/commonPermissionService';
import { CommonRootState } from 'common/store/typings';
import {
    isNullOrUndefined, userIsEvaluator, userIsPlanner,
} from 'common/util/utils';
import store from '../../store';
import { RootState } from '../../store/rootReducer';
import { GradingStatus, WorkflowState } from '../../store/context/types';
import {
    Committee, getCommitteeById, getEvaluators, getUserRolesInSelectedCommittee,
} from '../committeeService';
import {
    getCurrentCandidateLevelWorkflowState,
    getEvaluationRoundByCommitteeId, getPartyCommitteeIdForContext,
    GradingPartyDocument,
} from '../gradingPartyService';
import searchQueryBuilderService from '../search/searchQueryBuilderService';
import { CommitteeType } from '../../store/assessment/constants';
import { CandidateLevelWorkflowState, ExternalDeliveryStatus } from '../../store/common/constants';
import {
    getGradingLevelForTestOrCommittee,
    getMarksModeForTestOrCommittee,
    GRADING_LEVEL,
    GRADING_MODE, isGradingLockedForCandidate,
} from '../workflowService';
import { Evaluator } from '../../store/assessment/reducer';
import { isApParentTaskWithNotWeightedPoints } from '../../features/workflowSettings/services/workflowSettingsService';
import { isMultipleAttemptsChildTest, isMultipleAttemptsParentTest } from '../assessmentService';
import { MAProgressionLogic } from '../../store/assessment/MAProgressionLogic';
import { ASSESSMENTRUNQUESTION_INDEX, USERQUESTION_INDEX } from '../../store/common/search/constants';
import { AI_ASSISTED_MARKING_COLUMN_REF } from '../../features/aiSupportedMarkingIntro/guard';

export enum Actions {
    SET_SCORE = 'setScore',
    SET_GRADE = 'setGrade',
    RESET_SCORE = 'resetScore',
    STORE_PRIVATE_NOTES_QUESTION = 'storePrivateNotesQuestion',
    STORE_PRIVATE_NOTES_CANDIDATE = 'storePrivateNotesCandidate',
    STORE_GRADE_EXPLANATION = 'storeGradeExplanation',
    SET_FLAG_USER_QUESTION_EVALUATION = 'setFlagForUserQuestionEvaluation',
    SET_FLAG_USER_ASSESSMENT_EVALUATION = 'setFlagForUserAssessmentEvaluation',
    SET_CUSTOM_HTML_CONTENT = 'setCustomHtmlContent',
    CANCEL_REQUEST_OF_EXPLANATION = 'cancelRequestOfExplanation',
    EDIT_DUE_DATE_OF_EXPLANATION = 'editDueDateOfExplanation',
    ATTACH_FILE_FOR_EXPLANATION = 'attachFileForExplanation',
    ADD_NEW_CRITERIA_COMMENT = 'addNewCriteriaComment',
    EDIT_CRITERIA = 'editCriteria',
    EDIT_CRITERIA_POSITION = 'editCriteriaPosition',
    APPLY_CRITERIA = 'applyCriteria',
    DELETE_CRITERIA = 'deleteCriteria',
    EDIT_ANSWER_KEYS = 'editAnswerKeys',
    DOWNLOAD_SUBMISSION_FILE = 'downloadSubmissionFile',
    SEND_EXPLANATION_TO_ALL_CANDIDATES = 'sendExplanationToAllCandidates',
}

export const getCurrentUserPrivileges = (): string[] => {
    const { session } = (store.getState() as CommonRootState);
    const { user } = session;
    const { privileges, isStudent, hasExtendedAccess } = user;
    const listOfPrivileges = privileges ? [...privileges] : [];
    if (hasExtendedAccess && !listOfPrivileges.includes(UserPrivileges.ar_plan)) {
        listOfPrivileges.push(UserPrivileges.ar_plan);
    } else if (isStudent) {
        listOfPrivileges.push(UserPrivileges.student);
    }
    return listOfPrivileges;
};

export const hasCurrentUserPlannerPrivilege = (): boolean => {
    const userPrivileges = getCurrentUserPrivileges();

    return userIsPlanner(userPrivileges);
};

export const hasCurrentUserEvaluatorPrivilege = (): boolean => {
    const userPrivileges = getCurrentUserPrivileges();
    return userIsEvaluator(userPrivileges);
};

export const userIsStudent = (): boolean => {
    const { session } = (store.getState() as RootState);
    const { user } = session;
    const { isStudent } = user;
    return isStudent;
};

export const isGradingCompleteForCommitteeOrTest = (committeeId?: number): boolean => {
    const { assessment } = (store.getState() as RootState);
    if (committeeId) {
        const committee: Committee = getCommitteeById(committeeId);
        if (committee) {
            return committee.gradingCompleted;
        }
    }
    return assessment.assessmentRunGradingStatus === GradingStatus.GRADED;
};

export const isGradingReadyForParty = (gradingPartyDocument: GradingPartyDocument, isScoreGradeAction?: boolean): boolean => {
    const { assessment } = (store.getState() as RootState);

    // going with the committee grading status check instead of the candidateInfo.gradingCompleteOnCandidate
    // as the committee info comes from the db/cache which is more reliant than the index
    if (gradingPartyDocument && gradingPartyDocument.committeeId) {
        const candidateCommittee: Committee = getCommitteeById(gradingPartyDocument.committeeId);
        if (candidateCommittee) {
            // need to be specific about this
            return candidateCommittee.gradingCompleted === false
                && (assessment.isGradingReady
                    || (assessment.isGradingReadyWithoutEndTime
                        && getGradingLevelForTestOrCommittee(candidateCommittee.committeeId) === GRADING_LEVEL.CANDIDATE)
                    || (isScoreGradeAction && candidateCommittee.gradingAllowedForCommittee)
                    || candidateCommittee.type === CommitteeType.reassessment);
        }
    }

    return (
        assessment.isGradingReady
            && (assessment.assessmentRunGradingStatus === GradingStatus.GRADING_IN_PROGRESS
                || assessment.assessmentRunGradingStatus === GradingStatus.FINISHED
                || assessment.isGradingBeforeEndTimeEnabled))
        || (assessment.isGradingReadyWithoutEndTime && getGradingLevelForTestOrCommittee() === GRADING_LEVEL.CANDIDATE);
};

export const isJaggedStatusVisible = (): boolean => {
    const { assessment } = (store.getState() as RootState);
    return !assessment.hideJaggedStatus;
};

// The AI-supported marking MPP is checked in the column definitions, this adds the test setting.
const isColumnAllowedOnTest = (column: any): boolean => (
    column?.ref !== AI_ASSISTED_MARKING_COLUMN_REF
    || Boolean((store.getState() as RootState).assessment?.isAIAssistedMarkingEnabled)
);

export const isGradingComplete = (gradingPartyDocument: GradingPartyDocument): boolean => gradingPartyDocument?.gradingCompleteOnCandidate;

export const isCandidateCoveredByCurrentUser = (gradingPartyDocument: GradingPartyDocument): boolean => {
    const { assessment } = (store.getState() as RootState);
    const { context: { selectedCommitteeId } } = (store.getState() as RootState);

    const { committees } = assessment;
    if (committees) {
        if (selectedCommitteeId !== -1 && gradingPartyDocument.committeeId !== selectedCommitteeId) {
            return false;
        }
        const candidateCommittee = committees.find((committee: any) => {
            return committee.committeeId === gradingPartyDocument.committeeId;
        });

        return !!candidateCommittee && candidateCommittee.belongs;
    }
    return hasCurrentUserEvaluatorPrivilege();
};

export const wasCandidateCoveredByCurrentUserInPrimaryRound = (gradingPartyDocument: GradingPartyDocument): boolean => {
    const { assessment } = (store.getState() as RootState);
    const { context: { selectedCommitteeId } } = (store.getState() as RootState);

    const { committees } = assessment;
    if (committees) {
        if (selectedCommitteeId > 0 && gradingPartyDocument && gradingPartyDocument.committeeId !== selectedCommitteeId) {
            const candidateCommittee = committees.find((committee: any) => {
                return committee.committeeId === selectedCommitteeId;
            });
            const roundForCommittee = getEvaluationRoundByCommitteeId(gradingPartyDocument, selectedCommitteeId);
            return !!candidateCommittee && candidateCommittee.belongs
                && !!roundForCommittee && roundForCommittee.evaluationRound === 1;
        }
        // when looking at a candidate in his initial committee
        return isCandidateCoveredByCurrentUser(gradingPartyDocument);
    }
    return hasCurrentUserEvaluatorPrivilege();
};

const isChildAttemptShownInParentTest = (gradingPartyDocument: GradingPartyDocument): boolean => {
    const { assessment } = (store.getState() as RootState);
    return assessment.isMultipleAttemptsParent && gradingPartyDocument.isChild;
};

export const permissionService = {
    filterColumnsByPermissions: (columns: any[], dataType: string): any[] => {
        const userPrivileges = getCurrentUserPrivileges();
        const searchDefinitions: SearchColumn[] = searchQueryBuilderService.getColumnsDefinition(dataType);
        const { assessment } = (store.getState() as RootState);

        return columns.filter(isColumnAllowedOnTest).filter((column: any) => {
            const searchDef: SearchColumn = searchDefinitions
                .find((def: SearchColumn) => column.ref === def.ref || column.name === def.ref);
            if (searchDef) {
                if (searchDef.ref === 'candidate' && assessment.isMultipleAttemptsParent) {
                    column.expandable = true;
                }

                if (searchDef.ref === 'jaggedAnalysisStatus') {
                    return isJaggedStatusVisible();
                }
                if (searchDef.ref === 'explanationRequest') {
                    const { assessment } = (store.getState() as RootState);
                    return assessment?.explanationSettings?.explanationAdministrationEnabled;
                }
                const isAPlannerWorkspace: boolean = getWorkspaceType() === UserPrivileges.ar_plan;
                // for now disable the setGrade filter in planner workspaces
                if (searchDef.ref === 'setGrade' && isAPlannerWorkspace) {
                    return false;
                }

                if (searchDef.ref === 'finalGrade' && assessment?.isGradingWorkflowsEnabledForTest) {
                    return false;
                }

                if (searchDef.ref === 'candidateLevelWorkflowState' && assessment?.isGradingWorkflowsEnabledForTest) {
                    return false;
                }

                if ((searchDef.ref === 'gradesFromEvaluators'
                || searchDef.ref === 'finalGrade'
                || searchDef.ref === 'setGrade') && assessment?.isMarksOnlyExam) {
                    return false;
                }

                if (assessment.isAssessmentPath) {
                    if (searchDef.ref === 'finalGrade') {
                        return false;
                    }
                    if (searchDef.ref === 'gradingWeight' && isApParentTaskWithNotWeightedPoints()) {
                        return false;
                    }

                    if (searchDef.ref === 'finalPoints'
                        && !assessment.useGradingThresholds
                        && !assessment.isMarksOnlyExam) {
                        return false;
                    }
                }

                if (searchDef.ref === 'finalGradeValue' && !assessment?.isMarksOnlyExam) {
                    return false;
                }

                // "Assignee" column available only when question assignment enabled in current context
                if (searchDef.ref === 'assignments') {
                    return isQuestionAssignmentEnabled();
                }

                // hide the column if not using the grading threshold value
                if (searchDef.ref === 'maxScore') {
                    return assessment?.useGradingThresholds;
                }

                // hide the question override column if questionWeightOverrideEnabled is false
                if (searchDef.ref === 'questionWeight'
                    || searchDef.ref === 'defaultWeight'
                    || searchDef.ref === 'overriddenWeight') {
                    return assessment?.questionWeightOverrideEnabled;
                }

                // hide column if there is no plagiarism question type: [essay, fileUpload]
                // and hide column if no plagiarism service in use defined as mpp usePlagiarismService
                // also hide column if hidePlagiarismFromGraders flag set
                if (searchDef.ref === 'plagiarismScores') {
                    const shouldHide = assessment.hidePlagiarismFromGraders && !hasCurrentUserPlannerPrivilege();
                    return !shouldHide && ((assessment?.usePlagiarismService && assessment?.hasPlagiarismQuestionType)
                    || assessment?.externalLtiDeliveryStatus === ExternalDeliveryStatus.CONFIRMED);
                }

                if (searchDef.ref === 'ceqCandidateDeliveryStatus' && !assessment.isCEQTest) {
                    return false;
                }

                if (searchDef.internal || column.internal) {
                    return false;
                }

                if (isNullOrUndefined(searchDef.privilege)) {
                    return true;
                }
                if (!searchDef.privilege.includes(getWorkspaceType() as UserPrivileges) && !isWorkflowOverlayOpen()) {
                    return false;
                }
                if (searchDef.isAPField && !assessment.isAssessmentPath) {
                    return false;
                }
                if (searchDef.isMultipleAttemptsField
                    && !isMultipleAttemptsParentTest()) {
                    return false;
                }
                if (searchDef.ref === 'newAttemptStatus') {
                    return assessment.isMAProgressionLogicEnabled
                        && assessment.compositeInfo?.multipleAttemptsProgressionLogic === MAProgressionLogic.MANUALLY_BY_GRADER;
                }
                if (searchDef.ref === 'additionalScore') {
                    return assessment.displayAdditionalScore;
                }
                if (searchDef.ref === 'allowNextAttempt') {
                    return isMultipleAttemptsChildTest()
                    && assessment.isMAProgressionLogicEnabled
                    && assessment.compositeInfo?.multipleAttemptsProgressionLogic === MAProgressionLogic.MANUALLY_BY_GRADER;
                }
                if (searchDef.ref === 'hasMaximaValidationIssue') {
                    return assessment.hasMaximaQuestions;
                }
                if (dataType === ASSESSMENTRUNQUESTION_INDEX
                    && (searchDef.ref === 'markingStatus' || searchDef.ref === 'evaluatorHasAllQuestionsMarked')) {
                    if (!assessment.isGraderViewMarkingStatusFilterEnabled) {
                        return false;
                    }
                }

                if (dataType === USERQUESTION_INDEX
                    && searchDef.ref === 'markingStatus') {
                    if (!assessment.isGraderViewMarkingStatusFilterEnabled) {
                        return false;
                    }
                }

                if (dataType === USERQUESTION_INDEX
                    && searchDef.ref === 'questionIsScored'
                    && getWorkspaceType() === UserPrivileges.ar_plan
                    && Boolean((window as any).marketplaceProperties?.ENABLE_FILTER_INCOMPLETE_MARKED_QUESTIONS)) {
                    return false;
                }

                return searchDef.privilege.some((priv: UserPrivileges) => userPrivileges.includes(priv));
            }
            return false;
        });
    },

    hasPermissionForAction: (action: string, gradingPartyDocument: GradingPartyDocument): boolean => {
        switch (action) {
        case Actions.SET_SCORE:
        case Actions.RESET_SCORE:
        case Actions.APPLY_CRITERIA:
            return !!gradingPartyDocument
                    && !gradingPartyDocument.gradingCompleteOnCandidate
                    && isCandidateCoveredByCurrentUser(gradingPartyDocument)
                    && isGradingReadyForParty(gradingPartyDocument, true)
                    && !isScoreOrGradeReadOnly(gradingPartyDocument)
                    && !isGradingLockedForCandidate(gradingPartyDocument)
                    && isQuestionIdValidForCurrentCandidate();

        case Actions.SET_GRADE:
            return (getWorkspaceType() === UserPrivileges.ar_evaluate
                        || isWorkflowOverlayOpen()
                        || isGradingReadyForParty(gradingPartyDocument, true))
                    && !!gradingPartyDocument
                    && !gradingPartyDocument.gradingCompleteOnCandidate
                    && isCandidateCoveredByCurrentUser(gradingPartyDocument)
                    && isGradingReadyForParty(gradingPartyDocument, true)
                    && !isScoreOrGradeReadOnly(gradingPartyDocument)
                    && !isGradingLockedForCandidate(gradingPartyDocument)
                    && !isChildAttemptShownInParentTest(gradingPartyDocument);

        case Actions.STORE_PRIVATE_NOTES_CANDIDATE:
        case Actions.STORE_PRIVATE_NOTES_QUESTION:
        case Actions.STORE_GRADE_EXPLANATION:
        case Actions.ATTACH_FILE_FOR_EXPLANATION:
            return hasCurrentUserEvaluatorPrivilege();

        case Actions.SET_FLAG_USER_ASSESSMENT_EVALUATION:
        case Actions.SET_FLAG_USER_QUESTION_EVALUATION:
            return gradingPartyDocument && isCandidateCoveredByCurrentUser(gradingPartyDocument);

        case Actions.SET_CUSTOM_HTML_CONTENT:
        case Actions.CANCEL_REQUEST_OF_EXPLANATION:
        case Actions.EDIT_DUE_DATE_OF_EXPLANATION:
            return hasCurrentUserPlannerPrivilege();

        case Actions.EDIT_CRITERIA:
        case Actions.EDIT_CRITERIA_POSITION:
        case Actions.DELETE_CRITERIA:
            return gradingPartyDocument
                && !gradingPartyDocument.gradingCompleteOnCandidate
                && (hasCurrentUserPlannerPrivilege() || hasCurrentUserEvaluatorPrivilege())
                && isGradingReadyForParty(gradingPartyDocument)
                && !isScoreOrGradeReadOnly(gradingPartyDocument);

        case Actions.ADD_NEW_CRITERIA_COMMENT:
            return gradingPartyDocument
                && !gradingPartyDocument.gradingCompleteOnCandidate
                && (hasCurrentUserPlannerPrivilege() || hasCurrentUserEvaluatorPrivilege())
                && !isScoreOrGradeReadOnly(gradingPartyDocument);
        default:
            return false;
        }
    },

};

export const getCurrentUserPrivilegesAsString = () => {
    const userRoles = getUserRolesInSelectedCommittee();

    return parsePrivilegesAsString(userRoles);
};

export const parsePrivilegesAsString = (userRole: string[]) => {
    return userRole
        ?.filter((p) => p === UserPrivileges.ar_evaluate || p === UserPrivileges.ar_plan || p === UserPrivileges.ar_report)
        ?.map((p: string) => {
            return `${tl(`grading.role.${p}`)} `;
        }).join(', ');
};

export const isScoreOrGradeReadOnly = (gradingPartyDocument: GradingPartyDocument): boolean => {
    const { session } = (store.getState() as RootState);
    const partyCommitteeIdForContext = getPartyCommitteeIdForContext(gradingPartyDocument);
    const isCandidateLevelGrading = getGradingLevelForTestOrCommittee(partyCommitteeIdForContext) === GRADING_LEVEL.CANDIDATE;

    if (isCandidateLevelGrading) {
        const candidateLevelWorkflowState = getCurrentCandidateLevelWorkflowState(gradingPartyDocument, session.user.id);

        return candidateLevelWorkflowState === CandidateLevelWorkflowState.PENDING_CONFIRM
            || candidateLevelWorkflowState === CandidateLevelWorkflowState.PENDING_SHARE
            || candidateLevelWorkflowState === CandidateLevelWorkflowState.CONFIRMED;
    }

    return isScoreOrGradeReadOnlyOnTestOrCommittee();
};

export const isScoreOrGradeReadOnlyOnTestOrCommittee = (): boolean => {
    const { context } = (store.getState() as RootState);
    const { workflowInfo } = context;
    const { evaluatorState } = workflowInfo;

    return evaluatorState === WorkflowState.FINAL_GRADING_CONFIRMED_WAITING
        || evaluatorState === WorkflowState.OWN_GRADING_CONFIRMED_WAITING
        || evaluatorState === WorkflowState.GRADING_COMPLETED;
};

// AI-supported marking is read-only whenever the built-in mark panel would be (see SetMark): no
// grader role, or no permission to set a score on this candidate (grading complete, confirmed, locked, ...).
export const isAiMarkingReadOnly = (gradingPartyDocument: GradingPartyDocument): boolean => (
    !hasCurrentUserEvaluatorPrivilege()
    || !permissionService.hasPermissionForAction(Actions.SET_SCORE, gradingPartyDocument)
);

export const isQuestionAssignmentEnabled = (): boolean => {
    const { assessment, context } = (store.getState() as RootState);
    const { selectedCommitteeId } = context;
    const { gradingQuestionAssignmentEnabled, isAutomatedTestConstructionEnabled } = assessment;

    if (!hasCurrentUserPlannerPrivilege() && !hasCurrentUserEvaluatorPrivilege()) {
        return false;
    }

    return gradingQuestionAssignmentEnabled
        && !isAutomatedTestConstructionEnabled
        && selectedCommitteeId > -1
        && getMarksModeForTestOrCommittee(selectedCommitteeId) === GRADING_MODE.COLLABORATIVE;
};

export const isQuestionIdValidForCurrentCandidate = (): boolean => {
    const { context, session } = (store.getState() as RootState);
    const { partyState, questionId, partyId } = context;

    // students only have access to the list questions api when the marks report is enabled,
    // so we can't check questions
    if (session.user.isStudent || isNullOrUndefined(questionId)) {
        return true;
    }

    const questionsList = partyState?.questionsList;
    if (isNullOrUndefined(questionsList)) {
        return false;
    }
    return questionsList.some((question: HitData) => question.questionId === questionId
        && question.partyId === partyId);
};

export const isUserGraderForCurrentPartyInSelectedCommittee = () => {
    const { session, context } = (store.getState() as RootState);
    const committeeIdForParty = getPartyCommitteeIdForContext(context?.partyState?.gradingPartyDocument);
    const evaluators = getEvaluators(committeeIdForParty);

    return evaluators?.some((evaluator: Evaluator) => evaluator.id === session.user.id);
};

export const hasCurrentUserExtendedAccess = (): boolean => {
    const { session } = (store.getState() as CommonRootState);
    const { user } = session;
    const { isStudent, hasExtendedAccess } = user || {};
    return Boolean(hasExtendedAccess) && !isStudent;
};

export const shouldRestrictVisibilityOfGraderInfo = (): boolean => {
    // if current user is a planner or has extended access, then we don't restrict visibility of grader info
    if (hasCurrentUserExtendedAccess() || hasCurrentUserPlannerPrivilege()) {
        return false;
    }
    // else if user has a grader role and mpp on, restrict visibility of grader info
    return hasCurrentUserEvaluatorPrivilege()
       && Boolean((window as any).marketplaceProperties?.DISABLE_COMMITTEE_VIEW_OPTION_FOR_GRADER);
};
